import React, { useEffect, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Button from 'components/Button/Button';
import {
  ErrorText,
  Heading,
  SecondaryText,
  SecondaryTitle
} from 'components/core/Typography';
import SelectionBox from './SelectionBox';
import { faRotateRight, faUserCheck } from '@fortawesome/pro-duotone-svg-icons';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import Signature from '../Signature';
import moment, { Moment } from 'moment';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    width: 500,
    padding: 25
  },
  modalTitle: {
    padding: '16px 22px',
    fontSize: 17,
    fontWeight: 700
  },
  select: {
    gap: '21px',
    margin: '5px 0px',
    display: 'flex',
    padding: 10,
    borderRadius: 8,
    '&:hover': {
      cursor: 'pointer',
      background: '#f6f7fa'
    }
  },
  selectID: {
    extend: 'select',
    flexDirection: 'column'
  },
  label: {
    fontSize: 15,
    fontWeight: 400
  },
  button: {
    width: 'calc(100% - 40px)',
    margin: 20,
    padding: 21
  },
  divider: {
    width: '100%',
    height: 1,
    background: '#a4a4a442',
    margin: '30px 0px'
  },
  selectionBoxes: {
    display: 'flex',
    gap: '20px',
    margin: '20px 0px'
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    justifyContent: 'flex-end'
  },
  dateInput: {
    border: '1px solid #e7e7e7',
    padding: 10,
    borderRadius: 6
  }
}));

type Props = {
  onRequest: (checked: boolean, isRenewal: boolean, idDate?: Moment) => void;
  dbsCost: number; // Cost of the DBS & Background check combined
  checkCost: number;
  renewalCost: number;
  userName: string;
  companyName: string;
  hasInitial: boolean;
  onClose: () => void;
  checksLeft: number;
  dbsLeft: number;
  renewalsLeft: number;
  managerName: string;
};

type CheckType = 'initial' | 'renewal';

export default function RequestBackgroundCheckModal({
  onRequest,
  userName,
  companyName,
  managerName,
  hasInitial,
  renewalCost,
  dbsCost,
  checkCost,
  checksLeft,
  dbsLeft,
  renewalsLeft,
  onClose
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const [checked, setChecked] = React.useState(false);
  const [checkType, setCheckType] = React.useState<CheckType>(
    hasInitial ? 'renewal' : 'initial'
  ); // People with an inital check can only request a renewal

  const [complianceChecks, setComplicanceChecks] = React.useState<{
    continuousEmployment: boolean;
    identity: boolean;
    upToDate: boolean;
  }>({
    continuousEmployment: false,
    identity: false,
    upToDate: false
  });

  const [IDDate, setIDDate] = React.useState<Moment | undefined>(undefined);

  const [errorText, setErrorText] = React.useState<string | undefined>(
    undefined
  );

  const formatter = Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  });

  const costings: Record<CheckType, number> = {
    initial: checked ? dbsCost : checkCost,
    renewal: renewalCost
  };

  let cost = formatter.format(costings[checkType] / 100);

  let checksLeftText = '';

  // Cost if free when there are checks remaining
  if (checkType === 'initial') {
    if (checked && dbsLeft > 0) {
      checksLeftText = `${dbsLeft} free initial checks (+dbs) remaining`;
      cost = formatter.format(0);
    } else if (checksLeft > 0) {
      checksLeftText = `${checksLeft} free initial checks remaining`;
      cost = formatter.format(0);
    }
  }
  if (checkType === 'renewal' && renewalsLeft > 0) {
    checksLeftText = `${renewalsLeft} free renewal checks remaining`;
    cost = formatter.format(0);
  }

  return (
    <div
      className={classes.container}
      onClick={(evt) => {
        evt.stopPropagation();
      }}
    >
      <Heading>Request background check</Heading>
      <SecondaryText>Request a background check for {userName}</SecondaryText>
      <div className={classes.divider} />
      <SecondaryTitle>Pick a background check</SecondaryTitle>
      <div className={classes.selectionBoxes}>
        <SelectionBox
          disabled={hasInitial}
          selected={checkType === 'initial'}
          icon={faUserCheck}
          title={'Initial Background check'}
          subtitle="Request a 5 year background check"
          boxText="Initial check"
          onClick={() => {
            setCheckType('initial');
          }}
        />
        <SelectionBox
          selected={checkType === 'renewal'}
          icon={faRotateRight}
          title={'Renewal'}
          subtitle="Request a 3 year background check renewal"
          boxText="Renewal"
          onClick={() => setCheckType('renewal')}
        />
      </div>
      <div className={classes.divider} />
      {checkType === 'renewal' ? (
        <>
          <div className={classes.selectID}>
            <div className={classes.label}>
              <SecondaryTitle>ID expiry date</SecondaryTitle>
              <SecondaryText>
                The date of expiry for {userName}'s ID document (e.g Passport,
                driving licence)
              </SecondaryText>
            </div>
            <input
              type="date"
              className={classes.dateInput}
              onChange={(e) => {
                setIDDate(moment.utc(e.target.value, 'YYYY-MM-DD'));
              }}
            />
          </div>
          <div className={classes.divider} />
          <div
            className={classes.select}
            onClick={() =>
              setComplicanceChecks({
                ...complianceChecks,
                continuousEmployment: !complianceChecks.continuousEmployment
              })
            }
          >
            <input
              type="checkbox"
              checked={complianceChecks.continuousEmployment}
              onChange={(e) =>
                setComplicanceChecks({
                  ...complianceChecks,
                  continuousEmployment: e.target.checked
                })
              }
            />
            <div className={classes.label}>
              <SecondaryTitle>
                {userName} has remained in continuous employment.
              </SecondaryTitle>
              <SecondaryText>
                I confirm that this person has remained in continuous employment
                with {companyName} with no unexplained gaps of more than 28
                days.
              </SecondaryText>
            </div>
          </div>
          <div
            className={classes.select}
            onClick={() =>
              setComplicanceChecks({
                ...complianceChecks,
                identity: !complianceChecks.identity
              })
            }
          >
            <input
              type="checkbox"
              checked={complianceChecks.identity}
              onChange={(e) =>
                setComplicanceChecks({
                  ...complianceChecks,
                  identity: e.target.checked
                })
              }
            />
            <div className={classes.label}>
              <SecondaryTitle>
                {userName}'s identity has not changed.
              </SecondaryTitle>
              <SecondaryText>
                I confirm that their identity has not changed (or where it has
                that this has been validated e.g. through sight of a marriage
                certificate, deed poll for change of name, etc.).
              </SecondaryText>
            </div>
          </div>
          <div
            className={classes.select}
            onClick={() =>
              setComplicanceChecks({
                ...complianceChecks,
                upToDate: !complianceChecks.upToDate
              })
            }
          >
            <input
              type="checkbox"
              checked={complianceChecks.upToDate}
              onChange={(e) =>
                setComplicanceChecks({
                  ...complianceChecks,
                  upToDate: e.target.checked
                })
              }
            />
            <div className={classes.label}>
              <SecondaryTitle>
                {userName}'s background check is up to date.
              </SecondaryTitle>
              <SecondaryText>
                I confirm that {userName}'s 5 year background check remains up
                to date.
              </SecondaryText>
            </div>
          </div>

          <div className={classes.divider} />
          <Signature name={managerName} />
        </>
      ) : (
        <div className={classes.select} onClick={() => setChecked(!checked)}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <div className={classes.label}>
            <SecondaryTitle>
              Include DBS check {formatter.format((dbsCost - checkCost) / 100)}
            </SecondaryTitle>
            <SecondaryText>
              Also request a DBS check for this candidate
            </SecondaryText>
          </div>
        </div>
      )}

      <div className={classes.divider} />
      <ErrorText style={{ textAlign: 'right' }}>{errorText}</ErrorText>
      <SecondaryTitle style={{ textAlign: 'right' }}>
        Total: {cost}
      </SecondaryTitle>
      <SecondaryText style={{ textAlign: 'right', width: '100%' }}>
        {checksLeftText}
      </SecondaryText>
      <Spacer vertical spacing={1} />
      <div className={classes.buttons}>
        <Button archetype="flat" onClick={onClose}>
          Cancel
        </Button>
        <Button
          archetype="flatPrimary"
          disabled={
            checkType === 'renewal' &&
            (!complianceChecks.upToDate ||
              !complianceChecks.identity ||
              !complianceChecks.continuousEmployment)
          }
          onClick={() => {
            if (!checkType) {
              setErrorText('Please select a background check type');
              return;
            }

            if (checkType === 'renewal') {
              if (
                !complianceChecks.continuousEmployment ||
                !complianceChecks.identity ||
                !complianceChecks.upToDate
              ) {
                setErrorText(
                  'Please confirm all compliance checks before proceeding'
                );
                return;
              }

              if (!IDDate || moment(IDDate).isBefore(moment())) {
                setErrorText('Please enter a valid ID expiry date');
                return;
              }
            }

            onRequest(checked, checkType === 'renewal', IDDate);
          }}
        >
          Request check
        </Button>
      </div>
    </div>
  );
}
